import { getDecorationItemConfig } from '../catalog/catalog.getters';
import type { State } from '../State';
import {
  DecorationRoomID,
  DecorationItem,
  decorationItemsMap,
  decorationItems,
  DecorationItemID,
  DecorationSlot,
  DecorationTier,
  tiersItemsMap,
  DecorationTierID,
} from './decoration.ruleset';
import { PurchasedItemState, SelectedItemState } from './decoration.state';

/*
 * Convert state SelectedItemState[] representation to Map of DecorationItem[] for room specific room
 */
export function getSelectedRoomDecorations(
  selected: SelectedItemState[],
  room: DecorationRoomID,
): Map<DecorationSlot, DecorationItem & { tierId?: DecorationTierID }> {
  return (
    selected
      .map((item) => ({
        id: item.id as DecorationItemID,
        tierId: item.tierId as DecorationTierID,
      }))
      .map(({ id, tierId }) => {
        const baseItem = getDecorationItem(id);
        const tierItem = getDecorationItemConfig(id, tierId);

        // If tierId is defined, then override base item with tier item.
        if (tierId && tierItem) {
          return {
            ...baseItem,
            image: tierItem.image,
            tierId,
          };
        }
        return baseItem;
      })
      // Remove undefined items in case items is not in config yet
      .filter((item) => !!item)
      .filter((items) => items.room === room)
      .reduce((acc, item) => acc.set(item.slot, item), new Map())
  );
}

/**
 * Get equipped decoration by id
 */
export function getSelectedItemByID(
  state: State,
  id: DecorationItemID,
): DecorationItem | undefined {
  const item = getSelectedDecorationsState(state).find(
    (items) => items.id === id,
  );
  if (!item) return undefined;

  const data = getDecorationItem(item.id as DecorationItemID);

  return data;
}

/**
 * Get items available for slot equipment
 */
export function getAvailableDecorations(
  room: DecorationRoomID,
): DecorationItem[] {
  // Filter selected items and return typed objects array
  const available = decorationItems
    .filter((items) => items.room === room)
    .map((item) => getDecorationItem(item.id));

  return available;
}

/**
 * Get decoration item by id
 */
export function getDecorationItem(id: DecorationItemID): DecorationItem {
  return decorationItemsMap.get(id)!;
}

/**
 * Get decoration item by id
 */
export function getDecorationItemBySlot(
  state: State,
  room: DecorationRoomID,
  slot: DecorationSlot,
): (DecorationItem & { tierId?: DecorationTierID }) | null {
  // Find requested item in selected array
  const item = getSelectedDecorationsState(state)
    .filter((items) => items.room === room)
    .find((item) => item.slot === slot);

  // Return item config
  if (item) {
    const itemId = item.id as DecorationItemID;
    const itemTier = item.tierId as DecorationTierID;
    const baseItem = getDecorationItem(itemId);

    // If tierId is defined, then override base item with tier item.
    if (item.tierId) {
      return {
        ...baseItem,
        image: getDecorationItemConfig(itemId, itemTier)?.image,
        tierId: itemTier,
      };
    }

    return baseItem;
  }

  // OR null
  return null;
}

/*
 * Check if item is purchased
 */
export function isDecorationPurchased(
  state: State,
  id: DecorationItemID,
): boolean {
  const stateData = getPurchasedDecorationsState(state)[id];

  // Purchase state does not exist, so item is not purchased.
  if (!stateData) return false;

  // Otherwise check if user has any tier of item.
  const tiers = Object.values(stateData.tier);
  return tiers.reduce((acc, { quantity }) => acc + quantity, 0) > 0;
}

/*
 * Get all selected decorations in pure state representation
 */
export function getSelectedDecorationsState(state: State): SelectedItemState[] {
  return state.decoration.selected as SelectedItemState[];
}

/*
 * Get all selected decorations in pure state representation
 */
function getPurchasedDecorationsState(
  state: State,
): Record<DecorationItemID, PurchasedItemState> {
  return state.decoration.purchased as Record<
    DecorationItemID,
    PurchasedItemState
  >;
}

/**
 * Get decoration item tier.
 * @param id
 * @param tierId
 * @returns
 */
export function getDecorationItemTier(
  id: DecorationItemID,
  tierId: DecorationTierID,
): DecorationTier {
  return tiersItemsMap[id][tierId];
}

/**
 * Get decoration tier by number.
 * @param id
 * @param tierNumber
 */
export function getDecorationTierByNumber(
  id: DecorationItemID,
  tierNumber: number,
): DecorationTier | undefined {
  return Object.values(tiersItemsMap[id] ?? {}).find(
    (tierItem) => tierItem.tier === tierNumber,
  );
}
/**
 * Get highest decoration tier.
 * @param id
 * @returns
 */
export function getHighestDecorationTier(id: DecorationItemID): number {
  return decorationItemsMap.get(id)!.max_tier;
}
